import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Phone, MessageCircle, Eye, ArrowLeft, Target, Calendar, Clock, AlertCircle, UserX } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { StatusBadge } from "../components/common/StatusBadge";
import { QuickActions } from "../components/common/QuickActions";
import { CallLogModal } from "../components/common/CallLogModal";
import { HistoryButton } from "../components/common/CustomerHistoryModal";
import { useAuth } from "../context/AuthContext";
import { apiRequest, formatPhone, formatDate } from "../lib/utils";
import { toast } from "sonner";

const getOriginalStatusBadge = (status) => {
  const styles = {
    due: { bg: "bg-yellow-100", text: "text-yellow-800", icon: Clock },
    overdue: { bg: "bg-orange-100", text: "text-orange-800", icon: AlertCircle },
    delayed: { bg: "bg-blue-100", text: "text-blue-800", icon: Calendar },
    no_show: { bg: "bg-red-100", text: "text-red-800", icon: UserX }
  };
  const style = styles[status] || styles.due;
  const Icon = style.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${style.bg} ${style.text}`}>
      <Icon className="w-3 h-3" />
      {status?.replace("_", " ").charAt(0).toUpperCase() + status?.replace("_", " ").slice(1)}
    </span>
  );
};

export const SDRFollowUpPage = () => {
  const navigate = useNavigate();
  const { user, isAdmin } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [callLogContact, setCallLogContact] = useState(null);

  useEffect(() => {
    fetchSDRFollowUps();
  }, [user?.id]);

  const fetchSDRFollowUps = async () => {
    try {
      // For CRE users, use cre_user_id to filter by their assigned branches
      const creParam = !isAdmin() && user?.id ? `?cre_user_id=${user.id}` : "";
      const data = await apiRequest(`/dashboard/sdr-follow-ups${creParam}`);
      setCustomers(data || []);
    } catch (error) {
      console.error("Failed to fetch SDR follow-ups:", error);
      toast.error("Failed to load SDR follow-ups");
    } finally {
      setLoading(false);
    }
  };

  const handleCall = (customer, skipDialer = false) => {
    // First open the phone dialer (unless skipped, e.g., from QuickActions which handles it)
    if (!skipDialer && customer.phone) {
      window.location.href = `tel:${customer.phone}`;
    }
    // Show call log modal after a brief delay to allow dialer to open
    setTimeout(() => {
      setCallLogContact({
        id: customer.id,
        name: customer.name,
        phone: customer.phone,
        status: customer.status,
        type: "customer",
        branch_id: customer.branch_id,
        tag: customer.tag
      });
    }, skipDialer ? 0 : 800);
  };

  return (
    <div className="space-y-6" data-testid="sdr-follow-up-page">
      {/* Header */}
      <div className="flex items-center gap-3">
        <Button 
          variant="ghost" 
          size="icon" 
          className="md:hidden"
          onClick={() => navigate("/calls")}
        >
          <ArrowLeft className="w-5 h-5" />
        </Button>
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-[#ED1C24]/10">
            <Target className="w-6 h-6 text-[#ED1C24]" />
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold">SDR Follow-up</h1>
            <p className="text-muted-foreground text-sm">
              {customers.length} customers from Servall CRM (rescheduled)
            </p>
          </div>
        </div>
      </div>

      {/* Desktop Table */}
      <Card className="bento-card hidden md:block">
        <CardContent className="p-0">
          <div className="data-table-container">
            <Table>
              <TableHeader>
                <TableRow className="table-high-density">
                  <TableHead>Name</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Original Status</TableHead>
                  <TableHead>Current Status</TableHead>
                  <TableHead>Next Due Date</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8">Loading...</TableCell>
                  </TableRow>
                ) : customers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                      No SDR follow-ups scheduled
                    </TableCell>
                  </TableRow>
                ) : (
                  customers.map((customer) => (
                    <TableRow key={customer.id} className="table-high-density" data-testid={`sdr-row-${customer.id}`}>
                      <TableCell className="font-medium">{customer.name}</TableCell>
                      <TableCell className="font-mono text-sm">{formatPhone(customer.phone)}</TableCell>
                      <TableCell>
                        {getOriginalStatusBadge(customer.original_status)}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={customer.status} />
                      </TableCell>
                      <TableCell className="text-sm">
                        {customer.next_due_date ? formatDate(customer.next_due_date) : "-"}
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex items-center justify-end gap-1">
                          <HistoryButton 
                            customerId={customer.id} 
                            customerName={customer.name}
                          />
                          <QuickActions
                            phone={customer.phone}
                            onCall={() => handleCall(customer, true)}
                          />
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Mobile Card View */}
      <div className="md:hidden space-y-3">
        {loading ? (
          <div className="text-center py-8 text-muted-foreground">Loading...</div>
        ) : customers.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            No SDR follow-ups scheduled
          </div>
        ) : (
          customers.map((customer) => (
            <Card key={customer.id} className="bento-card" data-testid={`sdr-card-${customer.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{customer.name}</p>
                    <p className="text-sm text-muted-foreground font-mono">{formatPhone(customer.phone)}</p>
                  </div>
                  {getOriginalStatusBadge(customer.original_status)}
                </div>
                <div className="flex items-center gap-2 text-xs text-muted-foreground mb-3">
                  <Calendar className="w-3 h-3" />
                  <span>Due: {customer.next_due_date ? formatDate(customer.next_due_date) : "Not scheduled"}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 text-green-600 border-green-200 hover:bg-green-50"
                    onClick={() => handleCall(customer)}
                  >
                    <Phone className="w-4 h-4 mr-1" />
                    Call
                  </Button>
                  <a
                    href={`https://wa.me/91${customer.phone?.replace(/\D/g, "")}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1"
                  >
                    <Button size="sm" variant="outline" className="w-full text-green-600 border-green-200 hover:bg-green-50">
                      <MessageCircle className="w-4 h-4 mr-1" />
                      WA
                    </Button>
                  </a>
                  <HistoryButton 
                    customerId={customer.id} 
                    customerName={customer.name}
                  />
                  <Button size="sm" variant="outline" onClick={() => navigate(`/customers/${customer.id}`)}>
                    <Eye className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Call Log Modal */}
      {callLogContact && (
        <CallLogModal
          open={!!callLogContact}
          onClose={() => setCallLogContact(null)}
          contact={callLogContact}
          onSuccess={() => {
            setCallLogContact(null);
            fetchSDRFollowUps();
          }}
        />
      )}
    </div>
  );
};

export default SDRFollowUpPage;
